// components/SubtitleComparison/ComparisonSubtitleSearch.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useVideo } from "../../context/VideoContext";
import {
  searchSubtitles,
  fetchSubtitleContent,
} from "../../services/subtitleSearchApi";
import LoadingModal from "../VideoPlayer/LoadingModal";
import "./ComparisonSubtitleSearch.css";
import { getLanguageColor, getLanguageName, getMainLanguageOptions } from "../../utils/languages";

const ComparisonSubtitleSearch = ({ isOpen, onClose, onSelect, defaultLanguage = "all" }) => {
  const { videoMetadata, isPlaying, pauseVideo } = useVideo();
  const searchResultsRef = useRef(null);

  // توقف ویدیو هنگام باز شدن مودال
  useEffect(() => {
    if (isOpen && isPlaying) {
      pauseVideo();
    }
  }, [isOpen, isPlaying, pauseVideo]);

  // حالت‌های فرم جستجو
  const [searchForm, setSearchForm] = useState({
    name: videoMetadata?.filmName || "",
    type: videoMetadata?.videoType || "movie",
    language: "all",
    season: videoMetadata?.season || "",
    episode: videoMetadata?.episode || "",
  });

  useEffect(() => {
    if (videoMetadata?.filmName && isOpen) {
      setSearchForm({
        name: videoMetadata.filmName || "",
        type: videoMetadata.videoType || "movie",
    language: defaultLanguage,
        season: videoMetadata.season || "",
        episode: videoMetadata.episode || "",
      });

      const timer = setTimeout(() => {
        handleSearch(1);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // حالت‌های نمایش
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedSubtitles, setSelectedSubtitles] = useState(new Set());
  const [downloading, setDownloading] = useState(false);

  // وقتی فرم تغییر می‌کنه
  useEffect(() => {
    if (searchForm.type === "movie") {
      setSearchForm((prev) => ({ ...prev, season: "", episode: "" }));
    }
  }, [searchForm.type]);

  // اسکرول به بالای نتایج
  const scrollToTop = useCallback(() => {
    if (searchResultsRef.current) {
      searchResultsRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, []);

  // تابع جستجو
  const handleSearch = async (page = 1) => {
    if (!searchForm.name.trim()) return;

    setLoading(true);
    try {
      const params = {
        name: searchForm.name,
        type: searchForm.type,
        language: searchForm.language,
      };

      if (searchForm.type === "series") {
        if (searchForm.season) params.season = searchForm.season;
        if (searchForm.episode) params.episode = searchForm.episode;
      }

      const data = await searchSubtitles(params, page);

      if (data.success) {
        setResults(data.data.docs);
        setTotalPages(data.data.totalPages);
        setCurrentPage(data.data.page);

        if (page === 1) {
          setTimeout(() => scrollToTop(), 100);
        }
      }
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setLoading(false);
    }
  };

  // انتخاب/حذف زیرنویس
  const handleToggleSubtitle = (subtitleId) => {
    setSelectedSubtitles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(subtitleId)) {
        newSet.delete(subtitleId);
      } else {
        newSet.add(subtitleId);
      }
      return newSet;
    });
  };

  // اضافه کردن ۵ زیرنویس اول از یک زبان که هنوز انتخاب نشدن
  const handleQuickAdd5 = (language) => {
    const availableSubs = results.filter(
      (sub) =>
        sub.subtitlesLanguage === language && !selectedSubtitles.has(sub._id)
    );

    const subsToAdd = availableSubs.slice(0, 5);

    if (subsToAdd.length > 0) {
      setSelectedSubtitles((prev) => {
        const newSet = new Set(prev);
        subsToAdd.forEach((sub) => newSet.add(sub._id));
        return newSet;
      });
    }
  };

  // محاسبه تعداد زیرنویس‌های انتخاب‌نشده برای هر زبان
  const getAvailableCount = (language) => {
    return results.filter(
      (sub) =>
        sub.subtitlesLanguage === language && !selectedSubtitles.has(sub._id)
    ).length;
  };

  // دانلود و اضافه کردن زیرنویس‌های انتخاب شده
  const handleDownloadSelected = async () => {
    if (selectedSubtitles.size === 0) return;

    setDownloading(true);
    try {
      const selectedSubs = results.filter((sub) =>
        selectedSubtitles.has(sub._id)
      );

      for (const sub of selectedSubs) {
        try {
          const content = await fetchSubtitleContent(
            sub.subtitlesUploadId || sub._id
          );
          const srtParser2 = (await import("srt-parser-2")).default;
          const parser = new srtParser2();
          const cues = parser.fromSrt(content);

          const newSub = {
            id: `online-${sub._id}`,
            source: "online",
            sourceType: "online",
            onlineId: sub._id,
            subtitlesUploadId: sub.subtitlesUploadId || sub._id,
            language: sub.subtitlesLanguage,
            subtitlesLanguage: sub.subtitlesLanguage,
            title: sub.nameOfMovie || "زیرنویس آنلاین",
            nameOfMovie: sub.nameOfMovie,
            movieName: sub.nameOfMovie,
            cues: cues,
            fileName: `${sub.nameOfMovie || "online"}.srt`,
            season: sub.season,
            episode: sub.episode,
            videoType: sub.type || "movie",
            otherTags: sub.otherTags || [],
            productionYear: sub.productionYear,
            coverUrl: sub.coverUrl,
            _source: "online_search",
          };

          onSelect(newSub);
        } catch (err) {
          console.error(`Error downloading subtitle ${sub._id}:`, err);
        }
      }

      onClose();
    } catch (error) {
      console.error("Error downloading subtitles:", error);
    } finally {
      setDownloading(false);
    }
  };

  if (!isOpen) return null;

  const hasResults = results.length > 0;
  const availableEn = getAvailableCount("en");
  const availableFa = getAvailableCount("fa");

  return (
    <>
      <div className="csearch-overlay">
        <div className="csearch-modal">
          {/* هدر */}
          <div className="csearch-header">
            <h2>جستجوی زیرنویس برای مقایسه</h2>
            <button className="csearch-close-btn" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="csearch-search-section">
            {/* نتایج */}
            <div className="csearch-search-results" ref={searchResultsRef}>
              {/* فرم جستجو */}
              <div className="csearch-form">
                <div className="csearch-form-row">
                  <div className="csearch-form-group">
                    <input
                      type="text"
                      value={searchForm.name}
                      onChange={(e) =>
                        setSearchForm({ ...searchForm, name: e.target.value })
                      }
                      placeholder="نام فیلم یا سریال"
                    />
                  </div>
                  <div className="csearch-form-group">
                    <select
                      value={searchForm.language}
                      onChange={(e) =>
                        setSearchForm({
                          ...searchForm,
                          language: e.target.value,
                        })
                      }
                    >
                      <option value="all">همه زبان‌ها</option>
                      {getMainLanguageOptions().map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    className="csearch-search-btn"
                    onClick={() => handleSearch(1)}
                    disabled={!searchForm.name.trim() || loading}
                  >
                    {loading ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fas fa-search"></i>
                    )}
                  </button>
                </div>

                <div className="csearch-form-row">
                  <div className="csearch-form-group">
                    <select
                      value={searchForm.type}
                      onChange={(e) =>
                        setSearchForm({ ...searchForm, type: e.target.value })
                      }
                    >
                      <option value="movie">فیلم</option>
                      <option value="series">سریال</option>
                    </select>
                  </div>

                  {searchForm.type === "series" ? (
                    <>
                      <div className="csearch-form-group">
                        <input
                          type="number"
                          value={searchForm.season}
                          onChange={(e) =>
                            setSearchForm({
                              ...searchForm,
                              season: e.target.value,
                            })
                          }
                          placeholder="فصل"
                        />
                      </div>
                      <div className="csearch-form-group">
                        <input
                          type="number"
                          value={searchForm.episode}
                          onChange={(e) =>
                            setSearchForm({
                              ...searchForm,
                              episode: e.target.value,
                            })
                          }
                          placeholder="قسمت"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="csearch-form-group csearch-hidden-input">
                      <input placeholder="" disabled />
                    </div>
                  )}
                </div>
              </div>

              {/* دکمه‌های انتخاب سریع - فقط وقتی نتیجه داریم */}
              {hasResults && (
                <div className="csearch-quick-add">
                  <button
                    className="csearch-quick-add-btn"
                    onClick={() => handleQuickAdd5("en")}
                    disabled={availableEn === 0}
                    title={`${availableEn} زیرنویس انگلیسی انتخاب‌نشده`}
                  >
                    <span
                      className="lang-dot"
                      style={{ backgroundColor: "#2196f3" }}
                    ></span>
                    +۵ انگلیسی
                    {availableEn > 0 && (
                      <span style={{ fontSize: "0.6rem", opacity: 0.7 }}>
                        ({availableEn} موجود)
                      </span>
                    )}
                  </button>
                  <button
                    className="csearch-quick-add-btn"
                    onClick={() => handleQuickAdd5("fa")}
                    disabled={availableFa === 0}
                    title={`${availableFa} زیرنویس فارسی انتخاب‌نشده`}
                  >
                    <span
                      className="lang-dot"
                      style={{ backgroundColor: "#4caf50" }}
                    ></span>
                    +۵ فارسی
                    {availableFa > 0 && (
                      <span style={{ fontSize: "0.6rem", opacity: 0.7 }}>
                        ({availableFa} موجود)
                      </span>
                    )}
                  </button>
                </div>
              )}

              {results.length > 0 ? (
                <>
                  <div className="csearch-results-list">
                    {results.map((subtitle) => {
                      const langColor = getLanguageColor(
                        subtitle.subtitlesLanguage
                      );
                      const isSelected = selectedSubtitles.has(subtitle._id);

                      return (
                        <div
                          key={subtitle._id}
                          className={`csearch-subtitle-item ${
                            isSelected ? "csearch-selected" : ""
                          }`}
                          onClick={() => handleToggleSubtitle(subtitle._id)}
                        >
                          <div className="csearch-subtitle-info">
                            <div className="csearch-main-info">
                              <span
                                className="csearch-language-badge"
                                style={{
                                  backgroundColor: langColor.bg,
                                  color: langColor.text,
                                }}
                              >
                                {getLanguageName(subtitle.subtitlesLanguage)}
                              </span>

                              <span className="csearch-name">
                                {subtitle.nameOfMovie}
                                {subtitle.season && subtitle.episode && (
                                  <span className="csearch-episode-info">
                                    S{subtitle.season}E{subtitle.episode}
                                  </span>
                                )}
                                <span
                                  className="csearch-subtitle-id"
                                  title="شناسه زیرنویس"
                                >
                                  {subtitle._id.slice(-6)}
                                </span>
                              </span>

                              {isSelected && (
                                <span
                                  className="csearch-selected-badge"
                                  title="انتخاب شده"
                                >
                                  ✓
                                </span>
                              )}
                            </div>

                            {subtitle.otherTags?.length > 0 && (
                              <div className="csearch-tags">
                                {subtitle.otherTags.map((tag) => (
                                  <span key={tag} className="csearch-tag">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="csearch-checkbox">
                            {isSelected && <i className="fas fa-check"></i>}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="csearch-pagination">
                      <button
                        disabled={currentPage === 1}
                        onClick={() => handleSearch(currentPage - 1)}
                      >
                        <i className="fas fa-chevron-right"></i>
                      </button>

                      <span>
                        صفحه {currentPage} از {totalPages}
                      </span>

                      <button
                        disabled={currentPage === totalPages}
                        onClick={() => handleSearch(currentPage + 1)}
                      >
                        <i className="fas fa-chevron-left"></i>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                !loading && (
                  <div className="csearch-no-results">
                    <i className="fas fa-search"></i>
                    <p>برای جستجو عبارت مورد نظر را وارد کنید</p>
                  </div>
                )
              )}
            </div>

            {/* فوتر */}
            {selectedSubtitles.size > 0 && (
              <div className="csearch-footer">
                <div className="csearch-selected-info">
                  <span className="csearch-selected-count">
                    {selectedSubtitles.size} زیرنویس انتخاب شده
                  </span>
                </div>

                <button
                  className="csearch-download-btn"
                  onClick={handleDownloadSelected}
                  disabled={downloading}
                >
                  {downloading ? (
                    <i className="fas fa-spinner fa-spin"></i>
                  ) : (
                    <i className="fas fa-download"></i>
                  )}
                  {downloading
                    ? "در حال دریافت..."
                    : `افزودن همه به مقایسه (${selectedSubtitles.size})`}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {downloading && <LoadingModal message="در حال دریافت زیرنویس..." />}
    </>
  );
};

export default ComparisonSubtitleSearch;
